import { useRouter } from 'expo-router'
import { useCallback, useMemo, useState, type ReactNode } from 'react'

import { ABUSE_TYPE } from '@/src/features/abuse/models/Abuse.enums'
import { ReportAbuseModal } from '@/src/features/abuse/ui/ReportAbuseModal/ReportAbuseModal'
import type { TUplift } from '@/src/features/intention/api/types/getIntentionPrayersApi.types'
import type { INTENTION_TYPE } from '@/src/features/intention/models/Intention.enums'
import { intentionMessageTypeMap } from '@/src/features/intention/models/IntentionMessageTypeMap'
import { intentionPrayerTypeMap } from '@/src/features/intention/models/IntentionPrayerTypeMap'
import { useIntentionPrayersQuery } from '@/src/features/intention/services/useIntentionPrayersQuery'
import { useCreateThankMessageMutation } from '@/src/features/message/services/useCreateThankMessageMutation'
import { useToast } from '@/src/general/components/ToastContext'
import { TEST_ID } from '@/src/general/constants/testId'
import { ICON } from '@/src/general/enums/icons'
import { useRoutes } from '@/src/general/utils/routes'
import { useTranslate } from '@/src/lang/useTranslate'

import { AnswerUpliftModal } from './components/AnswerUpliftModal'
import { ItemSeparatorComponent } from './components/ItemSeparatorComponent'
import { ListHeaderComponent } from './components/ListHeader'
import { PrayerRowPlaceholder } from './components/PrayerRowPlaceholder'
import { PrayersList } from './components/PrayersList/PrayersList'

const ListLoading = (): ReactNode =>
  Array(4)
    .fill(0)
    // oxlint-disable-next-line react/no-array-index-key
    .map((_, index) => <PrayerRowPlaceholder key={`prayer-placeholder-${index}`} />)

export type TIntentionPrayersScreenProps = {
  intentionId: number
  type: INTENTION_TYPE
}

export const IntentionPrayersScreen = ({ intentionId, type }: TIntentionPrayersScreenProps): ReactNode => {
  const [upliftInfosToBeAnswered, setUpliftInfosToBeAnswered] = useState<(TUplift & { prayerId: number }) | null>(null)
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false)
  const [reportedUpliftMessage, setReportedUpliftMessage] = useState<Omit<TUplift, 'isReported'> | null>(null)
  const {
    data,
    isPending: isIntentionPrayersPending,
    fetchNextPage,
    isFetchingNextPage,
    isError,
  } = useIntentionPrayersQuery(intentionId, intentionPrayerTypeMap[type])
  const { mutate } = useCreateThankMessageMutation({
    intentionId,
    type: intentionMessageTypeMap[type],
  })
  const { navigate } = useRouter()
  const { routes } = useRoutes()
  const { addToast } = useToast()
  const { t } = useTranslate()

  const handlePressReportAbuseUplift = ({ message, id }: TUplift) => {
    setIsModalVisible(true)
    setReportedUpliftMessage({ message, id })
  }

  const handleSeeConversation = (conversationId: number) => navigate(routes.conversationById(conversationId))

  const handleEndReached = useCallback(() => {
    if (!isFetchingNextPage && !isError) {
      fetchNextPage()
    }
  }, [isFetchingNextPage, isError, fetchNextPage])

  const renderHeader = useMemo(
    () => (
      <ListHeaderComponent
        intentionId={intentionId}
        isIntentionPrayersPending={isIntentionPrayersPending}
        isError={isError}
        type={type}
      />
    ),
    [intentionId, isIntentionPrayersPending, isError, type],
  )

  return (
    <>
      <PrayersList
        data={data}
        ItemSeparatorComponent={ItemSeparatorComponent}
        onEndReached={handleEndReached}
        onPressReportAbuseUplift={handlePressReportAbuseUplift}
        onSeeConversation={handleSeeConversation}
        onAnswer={setUpliftInfosToBeAnswered}
        onThank={mutate}
        testID={TEST_ID.INTENTION.PRAYERS.SCREEN.LIST}
        ListEmptyComponent={isIntentionPrayersPending ? ListLoading : null}
        ListHeaderComponent={renderHeader}
        ListFooterComponent={isFetchingNextPage ? PrayerRowPlaceholder : null}
      />
      <AnswerUpliftModal
        intentionId={intentionId}
        type={intentionMessageTypeMap[type]}
        onClose={() => {
          setUpliftInfosToBeAnswered(null)
        }}
        uplift={upliftInfosToBeAnswered}
        showModal={upliftInfosToBeAnswered !== null}
        onConfirm={() => {
          setUpliftInfosToBeAnswered(null)
        }}
      />
      {reportedUpliftMessage && (
        <ReportAbuseModal
          objectId={reportedUpliftMessage.id}
          objectType={ABUSE_TYPE.UPLIFT}
          onClose={() => {
            setIsModalVisible(false)
            setReportedUpliftMessage(null)
          }}
          onDone={() => {
            addToast({
              message: t('app.report-abuse.uplift.success'),
              colors: 'success',
              iconName: ICON.MESSAGE_INFO,
            })
          }}
          showModal={isModalVisible}
          text={reportedUpliftMessage.message}
        />
      )}
    </>
  )
}
